import { useCallback, useEffect, useRef, useState } from 'react';

// Use this hook to wrap calls to API functions in a check to make sure
//  the calling component is mounted when the API returns; if the component
//  isn't mounted, this hook will return undefined
export const useAPICall = <Arguments extends any[], Result extends any>(
  apiFunction: (...args: Arguments) => Result
) => {
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useRef(false);
  useEffect(() => {
    // Set ref to true when component mounts
    isMounted.current = true;
    // Set ref to false when component unmounts
    return () => {
      isMounted.current = false;
    };
  }, []);
  const safeCall = useCallback(
    async (...args: Arguments) => {
      setIsLoading(true);
      const data: Result = await apiFunction(...args);
      setIsLoading(false);
      if (isMounted.current) return data;
    },
    [isMounted, apiFunction]
  );
  return { safeCall, isLoading };
};
